import type { FundSummary } from '@nemo/api';
import {
  INVESTOR_TYPE_LABELS,
  formatRoundLabel,
  formatRoundMonth,
  investorTypeLabel,
  type RecentContactKind,
} from '@nemo/core/model';
import { z } from 'zod';
import { formatEur, formatWhen } from '../ui/format.js';
import type {
  CandidateAffinity,
  CandidatesInput,
  CandidateSort,
  CandidateSourceFilter,
  ContactFilter,
  DealroomRun,
  KnowAnyoneFilter,
  DealroomStatus,
  Round,
  RoundParticipant,
} from './types.js';

/** The six buckets a fund's type is mapped into; null until Dealroom matched it. */
export type InvestorType = NonNullable<FundSummary['investorType']>;

// Shared with the api's list export and share page, so they live in @nemo/core.
export { formatRoundLabel, formatRoundMonth, investorTypeLabel };

export const INVESTOR_TYPE_OPTIONS: readonly { id: string; label: string }[] = Object.entries(
  INVESTOR_TYPE_LABELS,
).map(([id, label]) => ({ id, label }));

/** Every bucket, in the same order `INVESTOR_TYPE_OPTIONS` lists them. */
export const ALL_INVESTOR_TYPES: readonly InvestorType[] = Object.keys(
  INVESTOR_TYPE_LABELS,
) as InvestorType[];

/**
 * A candidate's kind can also be nothing at all, and on production that is
 * almost all of them: the feed makes a candidate of every investor in every
 * round, and only the ones we went and fetched a profile for have a bucket.
 * "Unknown" is a value of the filter rather than the absence of one, because a
 * list of the six can never reach a name that is none of them.
 */
export const UNKNOWN_INVESTOR_TYPE = 'unknown';
/**
 * The buckets, with Dealroom's `corporate` split into a corporate's venture
 * arm (`corporate_vc`, shown by default) and the corporate itself.
 */
export type CandidateTypeFilter = NonNullable<CandidatesInput['types']>[number];

export const CANDIDATE_TYPE_OPTIONS: readonly { id: CandidateTypeFilter; label: string }[] = [
  ...ALL_INVESTOR_TYPES.flatMap((id): { id: CandidateTypeFilter; label: string }[] =>
    id === 'corporate'
      ? [
          { id: 'corporate_vc', label: 'Corporate VC' },
          { id, label: 'Corporate (not a venture arm)' },
        ]
      : [{ id, label: INVESTOR_TYPE_LABELS[id] }],
  ),
  { id: UNKNOWN_INVESTOR_TYPE, label: 'Unknown' },
];

/** The orders the candidates list offers, the default first. */
export const CANDIDATE_SORT_OPTIONS: readonly { id: CandidateSort; label: string }[] = [
  { id: 'overlap', label: 'Overlap' },
  { id: 'most_rounds', label: 'Most rounds' },
  { id: 'most_recent', label: 'Most recent' },
  { id: 'name', label: 'Name' },
  { id: 'know_anyone', label: 'Know anyone' },
  { id: 'last_contact', label: 'Last contact' },
];

/** "Contact in last 2 years / Lapsed / Never", the same three on Funds and Discover. */
export const CONTACT_OPTIONS: readonly { id: ContactFilter; label: string }[] = [
  { id: 'recent', label: 'Contact in last 2 years' },
  { id: 'lapsed', label: 'Lapsed (older contact)' },
  { id: 'never', label: 'Never' },
];

/** Where a Discovered fund came from. */
export const SOURCE_OPTIONS: readonly { id: CandidateSourceFilter; label: string }[] = [
  { id: 'dealroom', label: 'Funding rounds' },
  { id: 'person_move', label: 'Somebody moved there' },
  { id: 'affinity', label: 'Affinity contact' },
];

/** A last contact as a cell: "Mar 2026" when it is recent, a faint year when lapsed. */
export function lastContactText(contact: {
  at: string | null;
  nextMeetingAt?: string | null;
  recency: ContactFilter;
}): { text: string; dim: boolean } {
  switch (contact.recency) {
    case 'recent':
      return {
        text:
          contact.at !== null
            ? contactMonth(contact.at)
            : contact.nextMeetingAt !== null && contact.nextMeetingAt !== undefined
              ? `Meeting ${contactMonth(contact.nextMeetingAt)}`
              : '',
        dim: false,
      };
    case 'lapsed':
      return { text: contact.at === null ? '' : contactYear(contact.at), dim: true };
    case 'never':
      return { text: '—', dim: true };
    default: {
      const unreachable: never = contact.recency;
      return { text: String(unreachable), dim: false };
    }
  }
}

/** A recent contact that was not genuine, said faintly beside its date: "· emailed, unchecked". */
export function contactKindSuffix(contact: RecentContactKind | null | undefined): string {
  switch (contact) {
    case 'email_unchecked':
      return '· emailed, unchecked';
    case 'inbound_only':
      return '· inbound only';
    case 'yes':
    case null:
    case undefined:
      return '';
    default: {
      const unreachable: never = contact;
      return String(unreachable);
    }
  }
}

/** The hover on a last contact: the dates in full, and who. */
export function lastContactTip(contact: {
  at: string | null;
  nextMeetingAt?: string | null;
  recency: ContactFilter;
  lastEmailAt?: string | null;
  lastMeetingAt?: string | null;
  people?: readonly string[];
}): string | null {
  if (contact.recency === 'never') return 'No contact on record.';
  const lines: string[] = [];
  if (contact.recency === 'lapsed') {
    lines.push('Lapsed: the last contact is more than two years old, so not a relationship now.');
  }
  if (contact.at !== null) lines.push(`Last contact: ${formatWhen(contact.at)}`);
  if (contact.lastEmailAt !== null && contact.lastEmailAt !== undefined)
    lines.push(`Last email: ${formatWhen(contact.lastEmailAt)}`);
  if (contact.lastMeetingAt !== null && contact.lastMeetingAt !== undefined)
    lines.push(`Last meeting: ${formatWhen(contact.lastMeetingAt)}`);
  if (contact.nextMeetingAt !== null && contact.nextMeetingAt !== undefined)
    lines.push(`Next meeting: ${formatWhen(contact.nextMeetingAt)}`);
  if (contact.people !== undefined && contact.people.length > 0)
    lines.push(`Project A: ${contact.people.join(', ')}`);
  return lines.join('\n');
}

/** The "Know anyone?" filter's values, in the column's own order. */
export const KNOW_ANYONE_OPTIONS: readonly { id: KnowAnyoneFilter; label: string }[] = [
  { id: 'known', label: 'Yes (met, or we wrote, in last 2 years)' },
  { id: 'email_unchecked', label: 'Emailed, unchecked' },
  { id: 'inbound_only', label: 'Inbound only' },
  { id: 'lapsed', label: 'Lapsed (older contact only)' },
  { id: 'not_known', label: 'No' },
  { id: 'no_match', label: 'Not in Affinity' },
  { id: 'ambiguous', label: 'Unclear (several in Affinity)' },
  { id: 'error', label: 'Check failed' },
  { id: 'unchecked', label: 'Not checked' },
];

/** "Mar 2026": the month of the last contact, which is all the column has room for. */
function contactMonth(at: string): string {
  return new Date(at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** "2019": a lapsed contact needs no more than its year. */
function contactYear(at: string): string {
  return String(new Date(at).getUTCFullYear());
}

/**
 * What the "Know anyone?" column says: the answer, and in the hint colour what
 * goes with it — "Yes · Mar 2026". Never checked is a faint dash.
 */
export function knowAnyoneText(affinity: CandidateAffinity): {
  text: string;
  faint: string;
  dim: boolean;
} {
  switch (affinity.status) {
    case null:
      return { text: '—', faint: '', dim: true };
    case 'known':
      if (affinity.recency !== 'recent') {
        return {
          text: 'Lapsed',
          faint: affinity.lastContactAt === null ? '' : `· ${contactYear(affinity.lastContactAt)}`,
          dim: true,
        };
      }
      switch (affinity.contact) {
        case 'email_unchecked':
          return { text: 'Emailed, unchecked', faint: '', dim: true };
        case 'inbound_only':
          return { text: 'Inbound only', faint: '', dim: true };
        case 'yes':
        case null:
          return {
            text: 'Yes',
            faint:
              affinity.lastContactAt === null ? '' : `· ${contactMonth(affinity.lastContactAt)}`,
            dim: false,
          };
        default: {
          const unreachable: never = affinity.contact;
          return { text: String(unreachable), faint: '', dim: false };
        }
      }
    case 'not_known':
      return { text: 'No', faint: '', dim: false };
    case 'no_match':
      return { text: 'Not in Affinity', faint: '', dim: false };
    case 'ambiguous':
      return { text: '?', faint: '', dim: false };
    case 'error':
      return { text: '!', faint: 'check failed', dim: true };
    default: {
      const unreachable: never = affinity.status;
      return { text: String(unreachable), faint: '', dim: false };
    }
  }
}

/** The hover: what Affinity said in full, and when it was asked. */
export function knowAnyoneTip(affinity: CandidateAffinity): string | null {
  if (affinity.status === null)
    return 'Not checked in Affinity yet. Select it and use Check Affinity.';
  const day = (at: string | null) => formatWhen(at);
  const lines: string[] = [];
  switch (affinity.status) {
    case 'known':
      lines.push(knownLine(affinity));
      if (affinity.sentEmailAt !== null)
        lines.push(`Last written to by Project A: ${day(affinity.sentEmailAt)}`);
      if (affinity.people.length > 0) lines.push(`Project A: ${affinity.people.join(', ')}`);
      if (affinity.lastEmailAt !== null) lines.push(`Last email: ${day(affinity.lastEmailAt)}`);
      if (affinity.lastMeetingAt !== null)
        lines.push(`Last meeting: ${day(affinity.lastMeetingAt)}`);
      if (affinity.nextMeetingAt !== null)
        lines.push(`Next meeting: ${day(affinity.nextMeetingAt)}`);
      break;
    case 'not_known':
      lines.push('In Affinity, with no email or meeting with anybody here.');
      break;
    case 'no_match':
      lines.push('Affinity holds no organisation at its website or under its exact name.');
      break;
    case 'ambiguous':
    case 'error':
      if (affinity.note !== null) lines.push(affinity.note);
      break;
    default: {
      const unreachable: never = affinity.status;
      lines.push(String(unreachable));
    }
  }
  if (affinity.note !== null && affinity.status === 'known') lines.push(affinity.note);
  lines.push(`Checked ${day(affinity.checkedAt)}`);
  return lines.join('\n');
}

export const ALL_CANDIDATE_TYPES: readonly CandidateTypeFilter[] = CANDIDATE_TYPE_OPTIONS.map(
  (option) => option.id,
);

/** A candidate's kind as a row shows it; nothing known is said, not left blank. */
export function candidateTypeLabel(type: string | null): string {
  return investorTypeLabel(type) || 'Unknown';
}

/** A round with no number attached is not a round of zero. */
/** Where a round came to us from, in words. */
export const ROUND_SOURCE_LABELS: Record<Round['source'], string> = {
  dealroom: 'Dealroom',
  evertrace: 'Evertrace',
  research: 'Research',
  manual: 'Manual',
};

export function formatRoundAmount(amountEur: number | null): string {
  return amountEur === null ? 'undisclosed' : formatEur(amountEur);
}

/** How many HQ countries get their own chip before the rest fold into one. */
const HQ_CHIPS_SHOWN = 3;

/** A two-letter ISO code as a flag, or nothing for a code that is not one. */
export function flagEmoji(code: string): string {
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return '';
  return String.fromCodePoint(...[...upper].map((letter) => 127397 + letter.charCodeAt(0)));
}

export interface HqCountryChip {
  code: string;
  /** Flag plus code, so the country still reads if the flag does not render. */
  label: string;
}

export interface HqCountriesDisplay {
  shown: readonly HqCountryChip[];
  overflow: { label: string; title: string } | null;
}

/**
 * A fund can carry a couple of dozen HQ countries — a development bank backs
 * everywhere. Only the first few, in the order Dealroom gave them, get a chip
 * of their own; the rest fold into one "+N more" that still names them, in
 * its `title`, rather than dropping them.
 */
export function formatHqCountries(countries: readonly string[]): HqCountriesDisplay {
  const shown = countries.slice(0, HQ_CHIPS_SHOWN).map((code) => ({
    code,
    label: [flagEmoji(code), code].filter((part) => part !== '').join(' '),
  }));
  const rest = countries.slice(HQ_CHIPS_SHOWN);
  const overflow =
    rest.length > 0 ? { label: `+${rest.length} more`, title: rest.join(', ') } : null;
  return { shown, overflow };
}

/**
 * The country an ISO code names, written out. A chip has room for a flag and
 * two letters; a labelled cell in the pane has room for "United Kingdom", and
 * a reader should not have to know that "AT" is Austria.
 */
const regionNames =
  typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

export function countryName(code: string): string {
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return code;
  try {
    return regionNames?.of(upper) ?? code;
  } catch {
    return code;
  }
}

/**
 * The HQ countries as full names for a cell that may wrap. The same few get
 * their own name as get their own chip; the rest fold into a "+N more" that
 * names them in its title rather than dropping them.
 */
export function hqCountryNames(countries: readonly string[]): {
  text: string;
  title: string | null;
} {
  const shown = countries.slice(0, HQ_CHIPS_SHOWN).map(countryName);
  const rest = countries.slice(HQ_CHIPS_SHOWN);
  const text = [...shown, ...(rest.length > 0 ? [`+${rest.length} more`] : [])].join(', ');
  return { text, title: rest.length > 0 ? rest.map(countryName).join(', ') : null };
}

/** The same prettifying, as one bounded string for a cell that cannot wrap. */
export function hqCountriesText(countries: readonly string[]): string {
  const { shown, overflow } = formatHqCountries(countries);
  const parts = shown.map((chip) => chip.label);
  if (overflow !== null) parts.push(overflow.label);
  return parts.join(', ');
}

/** This fund's side of a round, and who else was in it. */
export function participantsOf(
  round: Round,
  fundId: string,
): { ours: RoundParticipant[]; others: RoundParticipant[] } {
  return {
    ours: round.participants.filter((participant) => participant.fundId === fundId),
    others: round.participants.filter((participant) => participant.fundId !== fundId),
  };
}

/** "with Accel, Index Ventures and 3 others", or nothing when it was alone. */
export function othersPhrase(others: readonly RoundParticipant[], shown = 2): string {
  if (others.length === 0) return '';
  const names = others
    .slice(0, shown)
    .map((participant) => participant.fundName ?? participant.nameRaw);
  const rest = others.length - names.length;
  if (rest > 0) names.push(rest === 1 ? '1 other' : `${rest} others`);
  const last = names.pop();
  return names.length === 0 ? `with ${last}` : `with ${names.join(', ')} and ${last}`;
}

/**
 * What the feed run wrote down. `summary` is `unknown` on the wire because the
 * four jobs write four different shapes, so the one shape this line reads is
 * checked here rather than assumed.
 */
const feedRunSummary = z.object({
  feed: z.object({
    roundsSeen: z.number(),
    investmentsCreated: z.number(),
  }),
});

const RUN_NOUNS = {
  match: 'match',
  backfill: 'backfill',
  feed: 'feed',
  attribute: 'press read',
  articles: 'article read',
} as const satisfies Record<DealroomRun['kind'], string>;

/**
 * The feed is the run this line is about. `status` carries the newest run of
 * each kind, so the feed is always there to be found once it has run at all;
 * before that, the line says what did run rather than claiming nothing has.
 */
export function runPhrase(status: DealroomStatus): string {
  const newest = [...status.lastRunByKind].sort((left, right) =>
    (right.startedAt ?? '').localeCompare(left.startedAt ?? ''),
  );
  const run = newest.find((candidate) => candidate.kind === 'feed') ?? newest[0];
  if (run === undefined) return 'nothing has run yet';
  const noun = RUN_NOUNS[run.kind];

  switch (run.status) {
    case 'queued':
      return `${noun} queued…`;
    case 'running':
      return `${noun} running…`;
    case 'cancelled':
      return `last ${noun} was cancelled`;
    case 'failed':
      return `${noun} failed: ${run.error ?? 'no reason given'}`;
    case 'done': {
      const when = formatWhen(run.finishedAt);
      const found = feedRunSummary.safeParse(run.summary);
      if (!found.success) return `${noun} finished ${when}`;
      const { roundsSeen, investmentsCreated } = found.data.feed;
      return `fed ${when}: ${roundsSeen} rounds, ${investmentsCreated} new`;
    }
  }
}

/** What a known answer means, by whether the contact was genuine. */
function knownLine(affinity: CandidateAffinity): string {
  if (affinity.recency !== 'recent')
    return 'Somebody at Project A was in touch once, but not in the last two years: lapsed (Affinity).';
  switch (affinity.contact) {
    case 'email_unchecked':
      return 'Emails in the last two years, but nobody has checked whether anybody here wrote: not a Yes until checked (Affinity).';
    case 'inbound_only':
      return 'Only emails received in the last two years; nobody here wrote to them (Affinity).';
    case 'yes':
    case null:
      return 'Somebody at Project A met them, or wrote to them, in the last two years (Affinity).';
    default: {
      const unreachable: never = affinity.contact;
      return String(unreachable);
    }
  }
}
